import { eventAddress, isAddressable, isReplaceable, type NostrEvent } from "./types"

/**
 * In-memory event store.
 *
 * Keeps the newest version of replaceable/addressable events (profiles, contact
 * lists, relay lists, relay sets, relay reviews) plus a bounded set of immutable
 * events, and notifies React subscribers. This is the shared cache every page
 * reads from, which is what keeps relay subscriptions out of components.
 */
export class EventStore {
  private readonly events = new Map<string, NostrEvent>()
  private readonly replaceable = new Map<string, NostrEvent>()
  private readonly listeners = new Set<() => void>()
  private version = 0
  private notifyScheduled = false
  private readonly maxEvents: number

  constructor(options: { maxEvents?: number } = {}) {
    this.maxEvents = options.maxEvents ?? 5000
  }

  getVersion(): number {
    return this.version
  }

  get size(): number {
    return this.events.size
  }

  /** Adds an event. Returns true when the store changed. */
  add(event: NostrEvent): boolean {
    if (!event?.id) return false
    if (this.events.has(event.id)) return false

    if (isReplaceable(event.kind) || isAddressable(event.kind)) {
      const address = eventAddress(event)
      const existing = this.replaceable.get(address)
      if (existing) {
        if (existing.id === event.id) return false
        if (existing.created_at > event.created_at) return false
        this.events.delete(existing.id)
      }
      this.replaceable.set(address, event)
    }

    this.events.set(event.id, event)
    if (this.events.size > this.maxEvents) {
      const oldest = this.events.keys().next()
      if (!oldest.done) this.events.delete(oldest.value)
    }
    this.scheduleNotify()
    return true
  }

  addAll(events: NostrEvent[]): number {
    let added = 0
    for (const event of events) {
      if (this.add(event)) added += 1
    }
    return added
  }

  get(id: string): NostrEvent | undefined {
    return this.events.get(id)
  }

  has(id: string): boolean {
    return this.events.has(id)
  }

  getReplaceable(kind: number, pubkey: string, identifier = ""): NostrEvent | undefined {
    const address = isAddressable(kind) ? `${kind}:${pubkey}:${identifier}` : `${kind}:${pubkey}`
    return this.replaceable.get(address)
  }

  values(): NostrEvent[] {
    return [...this.events.values()]
  }

  /** Arrow function so it can be passed straight to `useSyncExternalStore`. */
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  private scheduleNotify(): void {
    if (this.notifyScheduled) return
    this.notifyScheduled = true
    queueMicrotask(() => {
      this.notifyScheduled = false
      this.version += 1
      for (const listener of this.listeners) {
        try {
          listener()
        } catch (error) {
          console.error("[event-store] listener failed", error)
        }
      }
    })
  }

  clear(): void {
    this.events.clear()
    this.replaceable.clear()
    this.scheduleNotify()
  }
}
